/**
 * `/liturgies/{id}` — the workspace overview.
 *
 * Its job is to answer one question honestly: **what is still outstanding, and what is not.**
 *
 * Only Constraints appear as blockers. An optional item nobody filled in is not listed, because it
 * is not a problem — it is recorded as unset and carried into the export as such. Inventing a
 * blocker would stop a parish finalizing a liturgy that is genuinely complete
 * (docs/engineering/03-lifecycle-and-states.md).
 *
 * ## Six independent reads, one page that does not jump
 *
 * The four main cards and the three aside cards resolve from six queries that finish whenever they
 * finish. Each renders a **structure-preserving skeleton** rather than a spinner or nothing at all,
 * which is what the canvas asks for: *"The Planning Section keeps its shape while loading so the
 * page does not collapse and reflow."* Six things settling independently is six chances to move the
 * page under somebody's cursor, and a reader who has just clicked Finalize is the worst person to
 * do that to.
 *
 * ## What this screen must not say
 *
 * **Nothing invented.** Where the canvas draws a fact no endpoint supplies, the slot is not filled
 * with something plausible — this is the screen a parish trusts to describe the true state of their
 * celebration, and a convincing placeholder here is worse than a blank.
 *
 * **Access in force keeps the two mechanisms apart.** A Capability Link is a bearer credential; a
 * delegation is authority held by an Account. They are counted separately, worded separately and
 * revoked separately, because merging them into one number is how somebody revokes the wrong one.
 * Neither is a roster role: serving as Cantor confers no authority, and the card says so.
 *
 * **No credential, ever.** The card counts links; it never renders one, not even masked. Only the
 * Access screen handles credentials, and even there they stay hidden until deliberately revealed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  fetchCollaborators,
  fetchLiturgy,
  fetchPlanning,
  finalizeLiturgy,
  openPlanning,
  type Blocker,
  type LiturgyDetail,
  type PlanningSectionView,
} from './api';
import { EmptyState, Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import { fetchCapabilities } from '../capabilities/api';
import { fetchHistory, summarize, type HistoryEntry, type RecordType } from '../history/api';
import { fetchRoster } from '../people/api';
import { fetchLiturgyPolicy } from '../policies/api';
import './liturgies.css';

export function OverviewScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id' });
  const { t } = useTranslation('common');

  const liturgy = useQuery({ queryKey: ['liturgy', id], queryFn: () => fetchLiturgy(id) });

  if (liturgy.isError) {
    const error = liturgy.error as ApiError;
    return (
      <div className="lit-overview">
        {/* A liturgy you may not see and one that does not exist look identical, deliberately. */}
        <Notice referenceId={error.correlationId}>{error.message}</Notice>
        <p style={{ fontSize: 13 }}>
          <Link to="/liturgies">{t('liturgies.back')}</Link>
        </p>
      </div>
    );
  }

  const detail = liturgy.data ?? null;
  const blockers = detail?.blockers ?? [];
  const queryClient = useQueryClient();
  const finalize = useMutation({
    mutationFn: () => finalizeLiturgy(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['liturgy', id] }),
  });

  return (
    <div className="lit-overview">
      <header className="lit-dash__head">
        <div>
          <p className="lit-eyebrow">{detail ? t(`lifecycle.${detail.lifecycle_state}`) : ' '}</p>
          <h1 className="lit-dash__title">
            {detail ? detail.title : <Skeleton className="lit-skel--title" />}
          </h1>
          {detail ? (
            <p className="lit-overview__meta">
              {detail.place ? <span>{detail.place} · </span> : null}
              <span>Owned by {detail.owner_name}</span>
            </p>
          ) : null}
        </div>

        <div className="lit-dash__acts">
          <Link className="lit-btn lit-btn--quiet" to="/liturgies/$id/exports" params={{ id }}>
            {t('workspace.exports', 'Export')}
          </Link>
          <Link className="lit-btn lit-btn--quiet" to="/liturgies/$id/access" params={{ id }}>
            {t('access.share.submit', 'Share access')}
          </Link>
          <button
            className="lit-btn lit-btn--primary"
            disabled={
              finalize.isPending || blockers.length > 0 || detail?.lifecycle_state !== 'draft'
            }
            onClick={() => finalize.mutate()}
          >
            {t('access.actions.finalize.label', 'Finalize')}
          </button>
        </div>
      </header>

      <WorkspaceTabs liturgyId={id} />

      {/*
        `1fr 320px`, from the canvas — and 320px here genuinely *is* `--aside-width`. The dashboard
        draws `1fr 292px` and the two screens differ on purpose (Ruling 20); do not unify them.

        `min-width: 0` on the main column is load-bearing. Without it a long Liturgy title refuses
        to shrink and pushes the aside off the grid.
      */}
      <div className="lit-ov__body">
        <div className="lit-ov__main">
          <CelebrationCard id={id} detail={detail} />
          <SectionProgressCard id={id} />
          <BlockersCard id={id} detail={detail} />
          <PeopleCard id={id} />
        </div>

        <aside className="lit-ov__aside" aria-label={t('overview.aside_label')}>
          <LifecycleCard id={id} detail={detail} />
          <SelectionPolicyCard id={id} />
          <ActivityCard id={id} />
          <AccessInForceCard id={id} />
        </aside>
      </div>
    </div>
  );
}

// ── the celebration ──────────────────────────────────────────────────────────

/**
 * Six facts about the celebration, and only facts the detail route actually returns.
 *
 * The canvas's own workspace header draws a Rite and an owner line beside the title. Neither is in
 * `GET /liturgies/:id` — nor is the Template Version this Liturgy pinned, in any form a person
 * could read — so neither appears here. The slot stays unbuilt rather than filled.
 *
 * **Date, time and timezone are three cells rather than one string** because a celebration's time
 * is wall-clock time in the parish's own zone, not an instant. A 10:00 Mass is at 10:00 for the
 * parish planning it, whoever is looking, and the zone that makes that true is a fact worth its own
 * cell rather than a suffix (docs/engineering/01-domain-model.md).
 */
function CelebrationCard({ id, detail }: { id: string; detail: LiturgyDetail | null }) {
  const { t } = useTranslation('common');

  const celebrated = detail ? formatCelebration(detail.celebration_local_datetime) : null;

  const facts: Array<{ key: string; label: string; value: string }> = detail
    ? [
        { key: 'date', label: t('overview.celebration.date'), value: celebrated?.date ?? '—' },
        { key: 'time', label: t('overview.celebration.time'), value: celebrated?.time ?? '—' },
        {
          key: 'timezone',
          label: t('overview.celebration.timezone'),
          value: detail.celebration_timezone ?? '—',
        },
        {
          key: 'place',
          label: t('overview.celebration.place'),
          value: detail.place ?? '—',
        },
        {
          key: 'lifecycle',
          label: t('overview.celebration.lifecycle'),
          value: t(`lifecycle.${detail.lifecycle_state}`),
        },
        {
          key: 'planning',
          label: t('overview.celebration.planning'),
          value: detail.is_planning_editable
            ? t('overview.celebration.planning_editable')
            : t('overview.celebration.planning_frozen'),
        },
      ]
    : [];

  return (
    <section className="lit-card">
      <div className="lit-card__head">
        <h2 className="lit-card__title">{t('overview.celebration.title')}</h2>
        <span className="lit-card__spacer" />
        <Link className="lit-card__link" to="/liturgies/$id/planning" params={{ id }}>
          {t('overview.celebration.open_planning')}
        </Link>
      </div>

      <div className="lit-facts">
        {detail
          ? facts.map((fact) => (
              <div key={fact.key} className="lit-fact">
                <div className="lit-fact__label">{fact.label}</div>
                <div className="lit-fact__value">{fact.value}</div>
              </div>
            ))
          : SKELETON_FACTS.map((n) => (
              <div key={n} className="lit-fact" aria-hidden="true">
                <Skeleton className="lit-skel--label" />
                <Skeleton className="lit-skel--value" />
              </div>
            ))}
      </div>
    </section>
  );
}

/** Six cells, matching the six facts, so the grid keeps its height while the query is in flight. */
const SKELETON_FACTS = [0, 1, 2, 3, 4, 5];

// ── section progress ─────────────────────────────────────────────────────────

/**
 * Approved Items per Section — the same arithmetic the Planning screen's own header does.
 *
 * "Approved" is `is_approved`, not `state`: a fresh draft can sit on top of an approved value, and
 * counting the draft would report a Section as having gone backwards when somebody merely started
 * editing.
 *
 * The bar takes one of two colours and no more. A ramp would assert a severity ordering the model
 * does not define — every Section is either fully approved or it is not, and how far along an
 * unfinished one is says nothing about whether anything here blocks Finalization. That question is
 * the next card's, and only Constraints answer it.
 */
function SectionProgressCard({ id }: { id: string }) {
  const { t } = useTranslation('common');
  const planning = useQuery({ queryKey: ['planning', id], queryFn: () => fetchPlanning(id) });

  const sections = planning.data?.sections ?? [];

  return (
    <section className="lit-card">
      <div className="lit-card__head">
        <h2 className="lit-card__title">{t('overview.progress.title')}</h2>
        <span className="lit-card__sub">{t('overview.progress.subtitle')}</span>
      </div>

      {planning.isPending ? (
        <SkeletonRows count={4} />
      ) : planning.isError ? (
        <p className="lit-card__state">{t('overview.progress.error')}</p>
      ) : sections.length === 0 ? (
        <div className="lit-card__empty">
          <EmptyState
            heading={t('overview.progress.empty_heading')}
            body={t('overview.progress.empty_body')}
          />
        </div>
      ) : (
        <ul className="lit-prog">
          {sections.map((section) => (
            <ProgressRow key={section.id} liturgyId={id} section={section} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ProgressRow({
  liturgyId,
  section,
}: {
  liturgyId: string;
  section: PlanningSectionView;
}): React.JSX.Element {
  const { t } = useTranslation('common');

  const total = section.items.length;
  const approved = section.items.filter((item) => item.is_approved).length;
  // An empty Section is complete rather than 0% — there is nothing in it left to approve.
  const percent = total === 0 ? 100 : Math.round((approved / total) * 100);
  const done = approved === total;

  return (
    <li>
      {/* The Planning screen anchors each Section as `#section-<key>`, so this lands on it. */}
      <Link
        className="lit-prog__row"
        to="/liturgies/$id/planning"
        params={{ id: liturgyId }}
        hash={`section-${section.key}`}
      >
        <span className="lit-prog__label">{section.label}</span>

        <span className="lit-prog__bar" aria-hidden="true">
          <span
            className={`lit-prog__fill${done ? ' lit-prog__fill--done' : ''}`}
            style={{ width: `${percent}%` }}
          />
        </span>

        <span className="lit-prog__count">
          {t('overview.progress.count', { count: total, approved })}
        </span>

        <span className={`lit-prog__pct${done ? ' lit-prog__pct--done' : ''}`}>{percent}%</span>
      </Link>
    </li>
  );
}

// ── before this Liturgy can be Finalized ─────────────────────────────────────

/**
 * The blocking card, unchanged in what it counts and extended in what it says.
 *
 * Every row is a Constraint, and both kinds block outright, so both are drawn identically —
 * colouring them differently would claim a severity ordering that does not exist.
 *
 * **The "where" is the surface that fixes it, not the Section.** `GET /liturgies/:id` returns a
 * blocker as `{ kind, label, explanation }` with no item id and no section, so a row cannot deep
 * link to the Item itself. What it can say truthfully is that an Item is fixed in Planning and a
 * Role in People, and the chevron is only drawn because that link genuinely goes somewhere.
 */
function BlockersCard({ id, detail }: { id: string; detail: LiturgyDetail | null }) {
  const { t } = useTranslation('common');
  const blockers = detail?.blockers ?? [];

  return (
    <section className="lit-card">
      <div className="lit-card__head">
        <h2 className="lit-card__title">{t('overview.blockers.title')}</h2>
        <span className="lit-card__spacer" />
        {detail ? (
          <span
            className={`lit-card__count${blockers.length === 0 ? ' lit-card__count--clear' : ''}`}
          >
            {blockers.length === 0
              ? t('overview.blockers.clear')
              : t('overview.blockers.open', { count: blockers.length })}
          </span>
        ) : null}
      </div>

      {!detail ? (
        <SkeletonRows count={3} />
      ) : blockers.length === 0 ? (
        <div className="lit-card__empty">
          <EmptyState
            heading={t('overview.blockers.empty_heading')}
            body={t('overview.blockers.empty_body')}
          />
        </div>
      ) : (
        <ul className="lit-blockers">
          {blockers.map((blocker) => (
            <BlockerRow key={`${blocker.kind}:${blocker.label}`} id={id} blocker={blocker} />
          ))}
        </ul>
      )}

      <p className="lit-card__footnote">{t('overview.blockers.footnote')}</p>
    </section>
  );
}

function BlockerRow({ id, blocker }: { id: string; blocker: Blocker }): React.JSX.Element {
  const { t } = useTranslation('common');
  const isRole = blocker.kind === 'role';

  return (
    <li>
      <Link
        className="lit-blocker"
        to={isRole ? '/liturgies/$id/people' : '/liturgies/$id/planning'}
        params={{ id }}
        search={blocker.kind === 'item' ? { item: blocker.item_id } : {}}
      >
        <span className="lit-blocker__bar" aria-hidden="true" />

        <span className="lit-blocker__body">
          <span className="lit-blocker__text">{blocker.label}</span>
          {blocker.explanation ? (
            <span className="lit-blocker__why">{blocker.explanation}</span>
          ) : null}
        </span>

        <span className="lit-blocker__where">
          {isRole ? t('overview.blockers.where_people') : t('overview.blockers.where_planning')}
        </span>

        <span className="lit-blocker__chevron" aria-hidden="true">
          ›
        </span>
      </Link>
    </li>
  );
}

// ── people ───────────────────────────────────────────────────────────────────

/**
 * Four counts from the roster, and deliberately not a list of names.
 *
 * Proposed and Confirmed are counted apart because they are different acts, exactly as Save and
 * Submit are: somebody suggesting a Cantor and the parish agreeing on one are not the same fact,
 * and one number covering both would let the screen report agreement nobody gave.
 *
 * None of these four is a permission. Who serves is recorded here; who may act comes only from a
 * Capability or a delegation, which is the aside's business and says so.
 */
function PeopleCard({ id }: { id: string }) {
  const { t } = useTranslation('common');
  const roster = useQuery({ queryKey: ['roster', id], queryFn: () => fetchRoster(id) });

  const roles = roster.data?.roles_in_use ?? [];
  const assignments = roles.flatMap((role) => role.assignments);

  const figures = [
    { key: 'roles', label: t('overview.people.roles'), value: roles.length },
    { key: 'people', label: t('overview.people.people'), value: roster.data?.people.length ?? 0 },
    {
      key: 'confirmed',
      label: t('overview.people.confirmed'),
      value: assignments.filter((a) => a.state === 'confirmed').length,
    },
    {
      key: 'proposed',
      label: t('overview.people.proposed'),
      value: assignments.filter((a) => a.state === 'proposed').length,
    },
  ];

  return (
    <section className="lit-card">
      <div className="lit-card__head">
        <h2 className="lit-card__title">{t('overview.people.title')}</h2>
        <span className="lit-card__spacer" />
        <Link className="lit-card__link" to="/liturgies/$id/people" params={{ id }}>
          {t('overview.people.manage')}
        </Link>
      </div>

      {roster.isError ? (
        <p className="lit-card__state">{t('overview.people.error')}</p>
      ) : (
        <div className="lit-figures">
          {figures.map((figure) => (
            <div key={figure.key} className="lit-figure">
              <div className="lit-figure__value">
                {roster.isPending ? <Skeleton className="lit-skel--figure" /> : figure.value}
              </div>
              <div className="lit-figure__label">{figure.label}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── the aside ────────────────────────────────────────────────────────────────

/**
 * Lifecycle, and the one act it offers.
 *
 * **Finalize is the fourth of four distinct acts, and it is unreachable while a Constraint is
 * open.** The button carries `disabled` on `blockers.length > 0` — inherited from the screen this
 * replaced and kept deliberately, because the server refusing it is the guarantee and this is only
 * the part the reader can see. What is added is the *reason*: a disabled control with no
 * explanation teaches people that the software is broken, so the count of what is still open sits
 * directly above it.
 *
 * The label is read from lifecycle state, never hardcoded. A draft is opened to collaborators; a
 * liturgy already frozen offers nothing, and renders no button rather than a dead one.
 *
 * The canvas's note — *"Planning remains editable…"* — is verbatim and is shown **only while that
 * is true**. Leaving it under a finalized liturgy would make the one sentence on the card a lie.
 */
function LifecycleCard({ id, detail }: { id: string; detail: LiturgyDetail | null }) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['liturgy', id] });

  const finalize = useMutation({ mutationFn: () => finalizeLiturgy(id), onSuccess: refresh });
  const open = useMutation({ mutationFn: () => openPlanning(id), onSuccess: refresh });

  const failure =
    (finalize.error ?? open.error) instanceof ApiError
      ? ((finalize.error ?? open.error) as ApiError)
      : undefined;

  const blockers = detail?.blockers.length ?? 0;
  const isDraft = detail?.lifecycle_state === 'draft';

  return (
    <section className="lit-ov-card lit-ov-card--state">
      <p className="lit-eyebrow">{t('overview.lifecycle_card.eyebrow')}</p>

      <p className="lit-ov-card__state">
        {detail ? (
          t(`lifecycle.${detail.lifecycle_state}`)
        ) : (
          <Skeleton className="lit-skel--state" />
        )}
      </p>

      {detail ? (
        <p className="lit-ov-card__note">
          {detail.is_planning_editable
            ? t('overview.lifecycle_card.note_editable')
            : t('overview.lifecycle_card.note_frozen')}
        </p>
      ) : null}

      {detail?.is_planning_editable && blockers > 0 ? (
        <p className="lit-ov-card__blocked">
          {t('overview.lifecycle_card.blocked', { count: blockers })}
        </p>
      ) : null}

      {/*
        Both acts stay offered — a draft with nothing outstanding really can be Finalized, and
        hiding the act would be the software deciding for the parish. What the state decides is
        which of the two is *the* cardinal one. This design reserves filled cardinal for a single
        primary action per screen, and stacking two of them would say the reader has two equally
        expected next steps when a draft plainly has one.
      */}
      {isDraft ? (
        <button
          type="button"
          className="lit-btn lit-btn--full"
          disabled={open.isPending}
          onClick={() => open.mutate()}
        >
          {open.isPending
            ? t('overview.lifecycle_card.opening')
            : t('overview.lifecycle_card.open')}
        </button>
      ) : null}

      {detail?.is_planning_editable ? (
        <button
          type="button"
          className={`lit-btn lit-btn--full${isDraft ? ' lit-btn--full-quiet' : ''}`}
          disabled={finalize.isPending || blockers > 0}
          onClick={() => finalize.mutate()}
        >
          {finalize.isPending
            ? t('overview.lifecycle_card.finalizing')
            : t('overview.lifecycle_card.finalize')}
        </button>
      ) : null}

      {failure ? <Notice referenceId={failure.correlationId}>{failure.message}</Notice> : null}
    </section>
  );
}

/**
 * Selection Policy summary — scope, restricted items, and active conflicts.
 */
function SelectionPolicyCard({ id }: { id: string }) {
  const { t } = useTranslation('common');
  const policy = useQuery({ queryKey: ['policy', id], queryFn: () => fetchLiturgyPolicy(id) });

  return (
    <section className="lit-card" aria-labelledby="heading-policy">
      <header className="lit-card__head">
        <h2 id="heading-policy" className="lit-card__title">
          Selection Policy
        </h2>
        <Link className="lit-card__more" to="/liturgies/$id/selection-policy" params={{ id }}>
          Configure →
        </Link>
      </header>

      {policy.isPending ? (
        <SkeletonRows count={3} />
      ) : policy.isError ? (
        <p className="lit-row__muted" style={{ padding: '8px 0' }}>
          Policy summary unavailable
        </p>
      ) : (
        <dl className="lit-ov__facts">
          <div className="lit-ov__fact">
            <dt className="lit-ov__fact-label">{t('liturgies.active_scope')}</dt>
            <dd className="lit-ov__fact-value">
              {policy.data.policy_name ?? 'Inherited defaults'}
            </dd>
          </div>
          <div className="lit-ov__fact">
            <dt className="lit-ov__fact-label">{t('liturgies.narrowed_items')}</dt>
            <dd className="lit-ov__fact-value">
              {policy.data.summary.narrowed_items} of {policy.data.summary.items}
            </dd>
          </div>
          <div className="lit-ov__fact">
            <dt className="lit-ov__fact-label">{t('liturgies.withheld_options')}</dt>
            <dd className="lit-ov__fact-value">{policy.data.summary.withheld_options}</dd>
          </div>
          {policy.data.conflicts.length > 0 ? (
            <div className="lit-ov__fact">
              <dt className="lit-ov__fact-label" style={{ color: 'var(--red)' }}>
                Active conflicts
              </dt>
              <dd className="lit-ov__fact-value" style={{ color: 'var(--red)', fontWeight: 600 }}>
                {policy.data.conflicts.length} conflict
                {policy.data.conflicts.length === 1 ? '' : 's'}
              </dd>
            </div>
          ) : null}
        </dl>
      )}
    </section>
  );
}

/**
 * Recent activity — the History tab's six record types, most recent first.
 *
 * A record type the requester may not see comes back as **null, not `[]`**, and null is dropped
 * here rather than rendered as an empty group: "there is nothing" and "this is not yours to see"
 * are different facts, and drawing the second discloses that something is being withheld.
 *
 * The bar's colour distinguishes *which register* an entry belongs to — a planning decision, a
 * lifecycle move, an access change — and never how serious it is. Recording that something happened
 * is not a judgement about it.
 */
function ActivityCard({ id }: { id: string }) {
  const { t, i18n } = useTranslation('common');
  const history = useQuery({ queryKey: ['history', id], queryFn: () => fetchHistory(id) });

  const entries = history.data ? recentEntries(history.data.data) : [];

  return (
    <section className="lit-ov-card">
      <p className="lit-eyebrow">{t('overview.activity.eyebrow')}</p>

      {history.isPending ? (
        <div className="lit-acts">
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} className="lit-skel--line" />
          ))}
        </div>
      ) : history.isError ? (
        <p className="lit-ov-card__note">{t('overview.activity.error')}</p>
      ) : entries.length === 0 ? (
        <EmptyState
          compact
          heading={t('overview.activity.empty_heading')}
          body={t('overview.activity.empty_body')}
        />
      ) : (
        <ul className="lit-acts">
          {entries.map((entry) => (
            <li key={`${entry.type}:${entry.entry.id}`} className="lit-act">
              <span className={`lit-act__bar lit-act__bar--${entry.type}`} aria-hidden="true" />
              <span className="lit-act__body">
                <span className="lit-act__text">
                  {summarize(entry.entry.summary, entry.type, t)}
                </span>
                <span className="lit-act__when">{relativeTime(entry.entry.at, i18n.language)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      <Link className="lit-ov-card__link" to="/liturgies/$id/history" params={{ id }}>
        {t('overview.activity.full_history')}
      </Link>
    </section>
  );
}

/** How many entries the aside shows. A panel that never ends stops being read. */
const ACTIVITY_LIMIT = 5;

/** Newest first across every record type the requester may see. */
function recentEntries(
  history: Record<RecordType, HistoryEntry[] | null>,
): Array<{ type: RecordType; entry: HistoryEntry }> {
  return (Object.entries(history) as Array<[RecordType, HistoryEntry[] | null]>)
    .flatMap(([type, entries]) => (entries ?? []).map((entry) => ({ type, entry })))
    .sort((a, b) => Date.parse(b.entry.at) - Date.parse(a.entry.at))
    .slice(0, ACTIVITY_LIMIT);
}

/**
 * Access in force — a summary, and a summary of **two things kept apart**.
 *
 * Capability Links and Account delegations are counted on separate lines under separate labels.
 * The canvas's own sample runs them together as *"2 Participant Links open · 1 delegated
 * Administrator"*; that reads well and it is the one thing this card must not do. They are
 * different mechanisms with different revocations, and a reader who has merged them in their head
 * revokes the wrong one during the incident where it matters.
 *
 * The canvas's third line names a person — *"María Álvarez holds Music-section authority by
 * delegation, recorded separately from her Cantor assignment."* No route here can supply a name:
 * `GET /liturgies/:id/collaborators` returns account ids, because planning has no port that turns
 * an Account into something a human reads and this screen is not the place to open one. So the
 * sentence keeps its *point* — a roster role is not a permission — and drops the invented name.
 *
 * Both reads want `manage_access`, and someone who lacks it gets a 404 from each. That is stated as
 * a fact about the system rather than about this Liturgy, so it discloses nothing.
 */
function AccessInForceCard({ id }: { id: string }) {
  const { t } = useTranslation('common');

  const grants = useQuery({ queryKey: ['capabilities', id], queryFn: () => fetchCapabilities(id) });
  const delegations = useQuery({
    queryKey: ['collaborators', id],
    queryFn: () => fetchCollaborators(id),
  });

  const pending = grants.isPending || delegations.isPending;
  const refused = grants.isError || delegations.isError;

  // Only links that still work. A revoked or expired grant is not access in force.
  const usable = (grants.data?.data ?? []).filter((grant) => grant.usable);
  const participant = usable.filter((grant) => grant.kind === 'participant').length;
  const administrator = usable.filter((grant) => grant.kind === 'administrator').length;
  const delegated = delegations.data?.data.length ?? 0;

  return (
    <section className="lit-ov-card lit-ov-card--warm">
      <p className="lit-eyebrow">{t('overview.access.eyebrow')}</p>

      {pending ? (
        <div className="lit-access">
          <Skeleton className="lit-skel--line" />
          <Skeleton className="lit-skel--line" />
        </div>
      ) : refused ? (
        <p className="lit-ov-card__note">{t('overview.access.error')}</p>
      ) : (
        <div className="lit-access">
          <div>
            <span className="lit-access__label">{t('overview.access.links_label')}</span>
            <span className="lit-access__value">
              {usable.length === 0
                ? t('overview.access.links_none')
                : `${t('overview.access.links_count', { count: usable.length })} · ${t(
                    'overview.access.links_kinds',
                    { participant, administrator },
                  )}`}
            </span>
          </div>

          <div>
            <span className="lit-access__label">{t('overview.access.delegations_label')}</span>
            <span className="lit-access__value">
              {delegated === 0
                ? t('overview.access.delegations_none')
                : t('overview.access.delegations_count', { count: delegated })}
            </span>
          </div>

          <p className="lit-access__note">{t('overview.access.separation')}</p>
        </div>
      )}

      <Link className="lit-ov-card__link" to="/liturgies/$id/access" params={{ id }}>
        {t('overview.access.manage')}
      </Link>
    </section>
  );
}

// ── loading ──────────────────────────────────────────────────────────────────

/**
 * A block that holds the space its content will take.
 *
 * `aria-hidden`, because it says nothing: a screen reader hears the surrounding card, not a
 * description of a grey rectangle.
 */
function Skeleton({ className }: { className?: string }): React.JSX.Element {
  return <span className={`lit-skel${className ? ` ${className}` : ''}`} aria-hidden="true" />;
}

function SkeletonRows({ count }: { count: number }): React.JSX.Element {
  return (
    <div aria-hidden="true">
      {Array.from({ length: count }, (_, n) => (
        <div key={n} className="lit-skel-row">
          <Skeleton className="lit-skel--label" />
          <Skeleton className="lit-skel--value" />
        </div>
      ))}
    </div>
  );
}

// ── formatting ───────────────────────────────────────────────────────────────

/**
 * "3 days ago", in the reader's language.
 *
 * A history entry's `at` is a real instant, unlike a celebration's wall-clock time, so it belongs
 * in the reader's own zone and their own idiom — `Intl` does both without a table of strings to
 * keep in step across two languages.
 */
function relativeTime(at: string, language: string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return '—';

  const seconds = Math.round((parsed - Date.now()) / 1000);
  const format = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });

  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size || unit === 'minute') {
      return format.format(Math.round(seconds / size), unit);
    }
  }

  return format.format(0, 'minute');
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * A celebration's time is **local wall-clock time in the parish's own zone**, not an instant. It is
 * rendered in that zone rather than the viewer's: a 10:00 Mass is at 10:00 for the parish planning
 * it, whoever happens to be looking (docs/engineering/01-domain-model.md).
 */
function formatCelebration(localDateTime: string | null): { date: string; time: string } | null {
  if (!localDateTime) return null;

  const parsed = new Date(localDateTime);
  if (Number.isNaN(parsed.getTime())) return null;

  // Stored as wall-clock, so rendered in UTC — which is how it was stored — rather than shifted
  // into the viewer's zone, which would move the time.
  return {
    date: parsed.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }),
    time: parsed.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    }),
  };
}
